/**
 * -----------------------------------------------------------------------------
 *
 * 	This component is an implementation of Polynomial Regression. This  
 *  model only supports having 1 single feature as a dependent variable (x)
 *  for yielding one output response (y).	   
 * 	
 * 	For theory, refer to: 
 * 		Book:`Chapra-Canale:Numerical Methods for Engineers, Sixth Edition`;
 * 		chapter: `Least-Squares Regression #17`; pag:`470`;
 * 		Read more here: https://www.originlab.com/doc/Origin-Help/PR-Algorithm
 * 
 * 	Code Reference: https://github.com/MenesesGHZ/polynomial-regression-js
 * ------------------------------------------------------------------------------
 */

import * as math from "mathjs";

class PolynomialRegression {
	constructor() {
		this.expression = "f(x) = undefined";
		this.coefficients = [];
		this.degree = NaN;
		this.f = (x) => 0;
	}

	train(X, y, degree = 1) {
		try {
			const n = X.length;
			let x_pow_deg = [],
				s_xiy = [];
			this.degree = degree;

			// Computing sum of Xs to the power of `i` -> [ X^0, X^1, ... , X^i ], and computing sums
			for (let i = 0; i <= this.degree; i++) {
				x_pow_deg.push(X.map(x => x ** i));
				s_xiy.push(math.multiply(x_pow_deg[i], y));
			}

			// Computing remaining sums for solving the `m+1` linear equations, where `m+1` is equal to the number of coeficients; m=this.degree
			for (let i = this.degree + 1; i <= 2 * this.degree; i++)
				x_pow_deg.push(X.map(el => el ** i));

			// Fixing equation; Ax = b -> A*s_xiy = coef	
			let A = math.zeros(this.degree + 1, this.degree + 1)._data
			for (let i = 0; i <= this.degree; i++)
				for (let j = 0; j <= this.degree; j++)
					A[i][j] = math.sum(x_pow_deg[j + i]);
			A[0][0] = n;

			// Solve linear equation Ax = b |  modeling f(x) in JS  |  fomating mathematical expression of the model
			this.coefficients = math.lusolve(math.lup(A), s_xiy)._data;
			this.f = (x) => {
				return math.sum(this.coefficients.map((coef, i) => coef * (x ** i)))
			};
			this.expression = "f(x) = ".concat(
				this.coefficients.map((el, i) => i === 0 ? `${(Math.round(el* 10) / 10).toFixed(1)}` : ` + (${(Math.round(el * 10) / 10).toFixed(1)}x^${i})`).join("")
			);

		} catch (error) {
			// Error message: Linear system cannot be solved since matrix is singular. |Matrix|=Det(Matrix)=0
			console.error(error);
		}
	}

	predict(X) {
		const response = Array.isArray(X) ? X.map((x) => this.f(x)) : this.f(X);
		return response;
	}
}

export default PolynomialRegression;
